/**
 * Mock marketplace — seeded catalog of 20 signed listings plus
 * publish/install handlers. Persisted to localStorage under a
 * separate key so the marketplace survives Vault resets.
 */

import type {
  AttestationRecord,
  BehavioralContract,
  CreatorProfile,
  DomainType,
  Hash256,
  Manifest,
  MarketplaceFilters,
  MarketplaceListing,
  MarketplaceReview,
  ProductListing,
  PublishInput,
  SignedEntity,
  SubscribeRequest,
  SubscribeResponse,
  TrustedTimestamp,
} from '../types';

import { appendActivity, getRecord, upsertRecord, type GiRecord } from './store';
import {
  canonicalHash,
  generateKeyPair,
  randomHex,
  signDigest,
} from './sign';

const STORAGE_KEY = 'gi-engine.marketplace.v1';
const MOCK_LATENCY_MS = 160;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface PersistedMarketplace {
  version: 1;
  listings: MarketplaceListing[];
  reviews: Record<string, MarketplaceReview[]>;
}

function emptyState(): PersistedMarketplace {
  return { version: 1, listings: [], reviews: {} };
}

function load(): PersistedMarketplace {
  if (typeof localStorage === 'undefined') return emptyState();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return emptyState();
  try {
    const parsed = JSON.parse(raw) as PersistedMarketplace;
    if (parsed.version !== 1) return emptyState();
    return parsed;
  } catch {
    return emptyState();
  }
}

function save(state: PersistedMarketplace): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ─── Public surface ────────────────────────────────────────────────

export async function listMarketplace(
  filters: MarketplaceFilters = {},
): Promise<MarketplaceListing[]> {
  await sleep(MOCK_LATENCY_MS);
  let listings = load().listings;
  if (listings.length === 0) {
    listings = seedDemoListings();
    save({ version: 1, listings, reviews: buildReviews(listings) });
  }
  return applyFilters(listings, filters);
}

export async function getMarketplaceListing(
  root_id: string,
): Promise<{ listing: MarketplaceListing; reviews: MarketplaceReview[] } | null> {
  await sleep(MOCK_LATENCY_MS);
  const state = load();
  const listing = state.listings.find((l) => l.root_id === root_id);
  if (!listing) return null;
  const reviews = state.reviews[root_id] ?? [];
  return { listing, reviews };
}

export async function publishToMarketplace(
  input: PublishInput,
): Promise<MarketplaceListing> {
  await sleep(MOCK_LATENCY_MS);
  const rec = getRecord(input.root_id);
  if (!rec) throw new Error('GI not found in the Vault.');

  const listing: MarketplaceListing = {
    root_id: rec.manifest.root_id,
    version: rec.manifest.version,
    name: rec.manifest.name,
    tagline: input.tagline,
    description: input.description,
    domain: rec.manifest.domain,
    author_name: rec.manifest.author.name,
    author_public_key: rec.manifest.author.public_key,
    published_at: { iso: new Date().toISOString(), source: 'Monotonic' },
    price_usd: Math.max(0, Math.floor(input.price_usd)),
    rating: 0,
    review_count: 0,
    installs: 0,
    certification: input.certification,
    contract: rec.contract,
    lineage: rec.lineage,
  };

  const state = load();
  const idx = state.listings.findIndex((l) => l.root_id === listing.root_id);
  if (idx >= 0) state.listings[idx] = listing;
  else state.listings.unshift(listing);
  save(state);

  // Mark the Vault record as 'published'.
  if (!rec.statuses.includes('published')) {
    rec.statuses = [...rec.statuses, 'published'];
    upsertRecord(rec);
  }
  appendActivity({
    kind: 'published',
    root_id: listing.root_id,
    name: listing.name,
    at: listing.published_at,
  });
  return listing;
}

export async function resetMarketplace(): Promise<void> {
  save(emptyState());
  await sleep(40);
}

// ─── Seeding ───────────────────────────────────────────────────────

const SEED_TEMPLATES: Array<{
  name: string;
  tagline: string;
  description: string;
  domain: DomainType;
  price_usd: number;
  rating: number;
  review_count: number;
  installs: number;
  certification: MarketplaceListing['certification'];
  version: string;
}> = [
  // Audio
  { name: 'amber-reverb', tagline: 'A warm plate reverb with tunable character.', description: 'Soft-plate reverb modelled after a 70s studio hardware unit. Decay, damping, and modulation expose as tunable parameters; every execution emits an attestation.', domain: 'Audio', price_usd: 49, rating: 4.6, review_count: 128, installs: 2_340, certification: 'silver', version: '1.4.0' },
  { name: 'orbit-compressor', tagline: 'Musical peak and RMS compressor.', description: 'Modern feedback compressor with automatic gain compensation. Good on drums, vocals, and busses.', domain: 'Audio', price_usd: 39, rating: 4.4, review_count: 88, installs: 1_460, certification: 'bronze', version: '0.9.2' },
  { name: 'cascade-eq', tagline: '6-band parametric equalizer.', description: 'Zero-latency minimum-phase EQ with tilted bell curves and shelf bakes.', domain: 'Audio', price_usd: 0, rating: 4.2, review_count: 412, installs: 18_320, certification: 'silver', version: '2.0.0' },
  // Process
  { name: 'fssc-batch-qa', tagline: 'Food-safety batch QA checklist.', description: 'FSSC 22000–aligned batch inspection pipeline. Every check emits a signed attestation the auditor can verify.', domain: 'Process', price_usd: 299, rating: 4.9, review_count: 24, installs: 186, certification: 'gold', version: '3.1.0' },
  { name: 'cold-chain-monitor', tagline: 'Continuous cold-chain attestation.', description: 'Watch temperature + humidity, cut an attestation every minute, flag drift above a threshold.', domain: 'Process', price_usd: 149, rating: 4.7, review_count: 58, installs: 420, certification: 'silver', version: '1.2.0' },
  { name: 'iso-9001-audit', tagline: 'One-click ISO 9001 audit pack.', description: 'Compiles a month of process attestations into a single exportable audit pack.', domain: 'Process', price_usd: 199, rating: 4.5, review_count: 19, installs: 94, certification: 'silver', version: '0.4.0' },
  // Medical
  { name: 'ekg-preprocess', tagline: 'AMAR-compliant EKG preprocessing.', description: 'Deterministic EKG preprocessing pipeline: filtering, segmentation, feature extraction. AMAR regulatory envelope.', domain: 'Medical', price_usd: 499, rating: 4.8, review_count: 12, installs: 41, certification: 'gold', version: '2.0.0' },
  { name: 'clinical-trial-ledger', tagline: 'FDA Part 11–compliant trial logging.', description: 'Every sample, every measurement, signed and timestamped. Part 11 audit trail out of the box.', domain: 'Medical', price_usd: 799, rating: 5.0, review_count: 6, installs: 17, certification: 'gold', version: '1.0.0' },
  { name: 'rad-dose-tracker', tagline: 'Radiation-dose chain of custody.', description: 'Tracks radiotracer dose from production to administration with a signed chain auditors can verify.', domain: 'Medical', price_usd: 0, rating: 4.3, review_count: 48, installs: 210, certification: 'bronze', version: '0.3.1' },
  // Financial
  { name: 'audit-batch', tagline: 'Amendment-13 compliant audit pack.', description: 'Signs every transaction window into an immutable pack with regulator-ready export.', domain: 'Financial', price_usd: 199, rating: 4.7, review_count: 87, installs: 1_120, certification: 'gold', version: '2.1.0' },
  { name: 'mifid-trade-log', tagline: 'MiFID II transaction reporting.', description: 'Signed, timestamped trade log formatted per MiFID II transaction-reporting rules.', domain: 'Financial', price_usd: 399, rating: 4.6, review_count: 33, installs: 112, certification: 'gold', version: '1.0.0' },
  { name: 'e-invoice-il', tagline: 'Israeli e-invoice pipeline.', description: 'Invoice generation, signing, and submission to the Israel Tax Authority endpoint.', domain: 'Financial', price_usd: 149, rating: 4.4, review_count: 214, installs: 8_400, certification: 'silver', version: '3.0.0' },
  // Defense
  { name: 'evidence-chain', tagline: 'Unbroken evidence chain of custody.', description: 'From scene to courtroom, every sample + handler signs. FRE 902(14) admissibility.', domain: 'Defense', price_usd: 0, rating: 4.9, review_count: 8, installs: 23, certification: 'silver', version: '0.5.0' },
  { name: 'secure-messaging', tagline: 'End-to-end signed field comms.', description: 'Every message signed, timestamped, chain-linked. Works over any transport.', domain: 'Defense', price_usd: 299, rating: 4.7, review_count: 15, installs: 58, certification: 'gold', version: '1.1.0' },
  // Government
  { name: 'permit-issuance', tagline: 'Municipal permit workflow.', description: 'Every permit a signed attestation. Trivial for regulators to reconstruct who approved what.', domain: 'Government', price_usd: 99, rating: 4.2, review_count: 71, installs: 340, certification: 'bronze', version: '0.7.0' },
  { name: 'vote-receipt', tagline: 'Verifiable vote-receipt issuer.', description: 'Per-voter receipt without revealing the vote. Zero-knowledge-ready (Phase 3).', domain: 'Government', price_usd: 0, rating: 4.5, review_count: 32, installs: 180, certification: 'silver', version: '0.2.0' },
  // Generic
  { name: 'pdf-signer', tagline: 'Drop-in PDF signer + verifier.', description: 'Signs and verifies PDFs under the GI signature chain — admissible alongside PAdES.', domain: 'Generic', price_usd: 49, rating: 4.3, review_count: 156, installs: 6_210, certification: 'bronze', version: '1.0.0' },
  { name: 'webhook-attestor', tagline: 'Sign every outbound webhook.', description: 'Drop this GI in front of any webhook source; every delivery comes with a signed receipt.', domain: 'Generic', price_usd: 29, rating: 4.0, review_count: 92, installs: 3_140, certification: 'bronze', version: '0.8.0' },
  { name: 'creator-shield', tagline: 'Instant proof-of-authorship.', description: 'Drop any creative file, get back a signed attestation — timestamped, unforgeable, yours forever.', domain: 'Generic', price_usd: 49, rating: 4.8, review_count: 842, installs: 32_600, certification: 'silver', version: '2.3.0' },
  { name: 'contract-signer', tagline: 'E-signature with full chain.', description: 'Every party signs; every signature anchors to the chain; verifier gets cryptographic receipt.', domain: 'Generic', price_usd: 29, rating: 4.4, review_count: 210, installs: 5_920, certification: 'bronze', version: '1.2.0' },
];

function seedDemoListings(): MarketplaceListing[] {
  // Each seed gets its own Ed25519 keypair + a real contract hash.
  // We DON'T insert these into the Vault — they're external GIs.
  return SEED_TEMPLATES.map((t, i) => buildListing(t, i));
}

function buildListing(
  t: (typeof SEED_TEMPLATES)[number],
  i: number,
): MarketplaceListing {
  const kp = generateKeyPair();
  const root_id = crypto.randomUUID();

  const contract: BehavioralContract = {
    inputs: [{ name: 'in', data_type: 'Document', required: true }],
    outputs: [{ name: 'out', data_type: 'Document', required: true }],
    parameters: [],
    behavior_rules: [],
    domain_adapter: t.domain,
    macros: [],
  };
  const contract_hash: Hash256 = canonicalHash(contract);

  const published_at: TrustedTimestamp = {
    iso: new Date(Date.now() - (i + 1) * 86_400_000 * 3).toISOString(),
    source: 'Monotonic',
  };

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: t.version,
    name: t.name,
    description: t.description,
    domain: t.domain,
    author: { name: pseudonym(i), public_key: kp.public_key },
    contract_hash,
    source_hash: `sha256:${randomHex(32)}`,
    wasm_hash: `sha256:${randomHex(32)}`,
    compiler: { name: 'mock-compiler', version: '0.1.0', target: 'wasm32-unknown' },
    lineage: { root_id, depth: 0 },
    created_at: published_at,
  };
  const manifest_hash = canonicalHash(manifest);
  const signature = signDigest(
    hexToBytes(manifest_hash.slice('sha256:'.length)),
    kp,
  );
  const signed_entity: SignedEntity = {
    root_id,
    version: t.version,
    manifest_hash,
    signature,
    signer_public_key: kp.public_key,
    created_at: published_at,
  };
  void signed_entity; // kept for parity with Vault records; not exposed on listings

  const lineage = {
    root_id,
    entries: [
      {
        index: 0,
        root_id,
        version: t.version,
        action: 'Genesis' as const,
        parent_hash: null,
        entry_hash: canonicalHash({ root_id, version: t.version, parent: null }),
        signer: kp.public_key,
        signed_at: published_at,
      },
    ],
  };

  return {
    root_id,
    version: t.version,
    name: t.name,
    tagline: t.tagline,
    description: t.description,
    domain: t.domain,
    author_name: manifest.author.name,
    author_public_key: kp.public_key,
    published_at,
    price_usd: t.price_usd,
    rating: t.rating,
    review_count: t.review_count,
    installs: t.installs,
    certification: t.certification,
    contract,
    lineage,
  };
}

const PSEUDONYMS = [
  'signalworks', 'atelier-labs', 'north-star', 'ironwood', 'mezzanine',
  'orbital-os', 'sigma-ops', 'ashtree', 'bluewave', 'lumen-co',
  'copperfold', 'meridian', 'hearth-labs', 'lattice-ai', 'koan-systems',
  'patina', 'prism', 'quorum', 'riverbend', 'solstice',
];
function pseudonym(i: number): string {
  return PSEUDONYMS[i % PSEUDONYMS.length]!;
}

// A single stock reviewer pool — reviews are seeded once and never
// updated (that's a later feature).
function buildReviews(listings: MarketplaceListing[]): Record<string, MarketplaceReview[]> {
  const out: Record<string, MarketplaceReview[]> = {};
  const REVIEW_TEXTS = [
    'Works as advertised. Saved our audit.',
    'Easy to install, attestations are beautiful.',
    'Minor rough edges; still a net win.',
    'Regulator loved the audit export. Would buy again.',
    'Good defaults. Parameters could be documented better.',
    'Exactly what we needed. Thank you.',
  ];
  for (const l of listings) {
    const count = Math.min(5, Math.max(0, Math.round(l.review_count / 20)));
    out[l.root_id] = Array.from({ length: count }).map((_, i) => ({
      id: `${l.root_id}-r${i}`,
      author_name: PSEUDONYMS[(i * 3) % PSEUDONYMS.length]!,
      rating: Math.max(3, Math.min(5, Math.round(l.rating + (i % 2 ? -0.5 : 0.5)))),
      body: REVIEW_TEXTS[i % REVIEW_TEXTS.length]!,
      at: { iso: new Date(Date.now() - (i + 1) * 86_400_000).toISOString(), source: 'Monotonic' },
    }));
  }
  return out;
}

// ─── Filter / sort ─────────────────────────────────────────────────

function applyFilters(
  listings: MarketplaceListing[],
  f: MarketplaceFilters,
): MarketplaceListing[] {
  let out = listings.slice();
  if (f.search) {
    const q = f.search.toLowerCase();
    out = out.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.tagline.toLowerCase().includes(q) ||
        l.description.toLowerCase().includes(q),
    );
  }
  if (f.domain) {
    out = out.filter((l) =>
      typeof l.domain === 'string' && typeof f.domain === 'string'
        ? l.domain === f.domain
        : false,
    );
  }
  if (f.certification) {
    out = out.filter((l) => l.certification === f.certification);
  }
  if (typeof f.min_rating === 'number') {
    out = out.filter((l) => l.rating >= f.min_rating!);
  }
  if (typeof f.max_price_usd === 'number') {
    out = out.filter((l) => l.price_usd <= f.max_price_usd!);
  }
  switch (f.sort ?? 'installs') {
    case 'newest':
      out.sort((a, b) => b.published_at.iso.localeCompare(a.published_at.iso));
      break;
    case 'rating':
      out.sort((a, b) => b.rating - a.rating);
      break;
    case 'price_asc':
      out.sort((a, b) => a.price_usd - b.price_usd);
      break;
    case 'price_desc':
      out.sort((a, b) => b.price_usd - a.price_usd);
      break;
    case 'installs':
    default:
      out.sort((a, b) => b.installs - a.installs);
  }
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

// Keep a silent reference so `GiRecord` import is always used.
void (null as GiRecord | null);
// signed_entity import kept for potential future "install to Vault" flow.
void (null as SignedEntity | null);
void (null as AttestationRecord | null);

// ─── Phase 17 — synthetic healthcare SaaS product ─────────────────

const CLINICAL_AUDIT_PUBKEY =
  'ed25519:a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2';

const CLINICAL_AUDIT_LISTING: ProductListing = {
  listing_id: 'lst_clinical_audit_flow_001',
  slug: 'clinical-audit-flow',
  name: 'Clinical Audit Flow',
  domain: 'Medical',
  tagline: 'Automated HIPAA-ready clinical audit trails for SaaS healthcare platforms.',
  description:
    'Clinical Audit Flow ingests EHR access logs, procedure records, and consent events ' +
    'and produces cryptographically signed audit bundles that satisfy HIPAA, SOC 2 Type II, ' +
    'and Joint Commission requirements. Every bundle ships with a QR-linked verifier so ' +
    'auditors can spot-check authenticity without installing software.',
  price_usd_cents: 79900,
  pricing_tiers: [
    {
      name: 'Starter',
      price_usd_cents: 79900,
      features: ['Up to 10,000 audit events/mo', 'HIPAA bundle export', 'Email support'],
    },
    {
      name: 'Growth',
      price_usd_cents: 199900,
      features: [
        'Unlimited audit events',
        'SOC 2 Type II pack',
        'Joint Commission export',
        'Priority support',
      ],
    },
    {
      name: 'Enterprise',
      price_usd_cents: 499900,
      features: [
        'Multi-tenant vaults',
        'Custom compliance profiles',
        'SLA 99.9%',
        'Dedicated CSM',
      ],
    },
  ],
  royalty_split: { author: 0.88, engine: 0.1, foundation: 0.02 },
  manifest_hash: 'sha256:0000000000000000000000000000000000000000000000000000000000000000',
  published_at: '2026-01-15T09:00:00Z',
  installs: 312,
  avg_rating: 4.8,
};

export async function getProductBySlug(slug: string): Promise<ProductListing> {
  await sleep(MOCK_LATENCY_MS);
  if (slug === 'clinical-audit-flow') return CLINICAL_AUDIT_LISTING;
  throw new Error(`Product not found: ${slug}`);
}

export async function subscribe(
  _slug: string,
  _req: SubscribeRequest,
): Promise<SubscribeResponse> {
  await sleep(MOCK_LATENCY_MS);
  return {
    checkout_url: 'https://checkout.stripe.com/test_session_001',
    session_id: 'cs_test_001',
  };
}

export async function getCreatorProfile(pubkey: string): Promise<CreatorProfile> {
  await sleep(MOCK_LATENCY_MS);
  return {
    pubkey,
    display_name: pubkey === CLINICAL_AUDIT_PUBKEY ? 'ClinicalOps Ltd.' : `key-${pubkey.slice(0, 8)}`,
    products: pubkey === CLINICAL_AUDIT_PUBKEY ? [CLINICAL_AUDIT_LISTING] : [],
    total_products: pubkey === CLINICAL_AUDIT_PUBKEY ? 1 : 0,
  };
}

// Keep type references live so TS doesn't prune the new imports.
void (null as SubscribeRequest | null);
void (null as SubscribeResponse | null);
void (null as CreatorProfile | null);
